import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Eye, FilePlus2, FileText, RefreshCw, RotateCcw, Save, Send, XCircle } from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { LoadError } from '../../../components/layout/LoadError';
import { ComplianceAlertList, type ComplianceItem } from '../../../components/ui/ComplianceAlert';
import { useConsequence } from '../../../components/ui/ConsequencePreview';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/toast';
import {
  useCancelDocument,
  useCreateDocumentVersion,
  useDocKitCatalog,
  useDocument,
  useDocumentEvents,
  useDocumentPdf,
  useDocumentPreview,
  usePatchDocument,
  usePublishDocument,
  useRefreshDocument,
  useReopenDocument,
  useRetryDocument,
  useReviewDocument,
} from '../../../hooks/doc-kit';
import { ApiError } from '../../../lib/api-client';
import type { AckMode, DocKitResponse, DocumentAudience, DocumentDetail as DocumentDetailType, DocumentRow, RenderedBlock } from '@payroll/shared';

const ACK_LABEL: Record<AckMode, string> = { none: 'ไม่ต้องรับทราบ', button: 'กดรับทราบ', signature: 'ลงนาม' };

function isUnavailable<T extends object>(data: DocKitResponse<T> | undefined): data is { unavailable: string } {
  return Boolean(data && 'unavailable' in data);
}

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function audienceLabel(audience: DocumentAudience): string {
  if (audience.scope === 'all') return 'พนักงานทุกคน';
  if (audience.scope === 'dept') return `หน่วยงานที่เลือก ${audience.dept_ids?.length ?? 0} หน่วยงาน`;
  if (audience.scope === 'branch') return `สาขาที่เลือก ${audience.branch_ids?.length ?? 0} สาขา`;
  return `พนักงานที่เลือก ${audience.emp_codes?.length ?? 0} คน`;
}

function splitValues(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function scopeValues(audience: DocumentAudience): string[] {
  if (audience.scope === 'dept') return audience.dept_ids ?? [];
  if (audience.scope === 'branch') return audience.branch_ids ?? [];
  if (audience.scope === 'emp') return audience.emp_codes ?? [];
  return [];
}

function buildAudience(scope: DocumentAudience['scope'], values: string): DocumentAudience {
  const ids = splitValues(values);
  if (scope === 'dept') return { scope, dept_ids: ids };
  if (scope === 'branch') return { scope, branch_ids: ids };
  if (scope === 'emp') return { scope, emp_codes: ids };
  return { scope: 'all' };
}

function dateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' });
}

function DetailSkeleton() {
  return <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดรายละเอียดเอกสาร"><div className="card h-36 bg-gray-100" /> <div className="card h-72 bg-gray-100" /></div>;
}

// backend (PATCH edits) รับเฉพาะบล็อกที่มี text — รายการ/ตาราง/ลงนามแก้ผ่านจอไม่ได้ในรอบนี้
function editableText(block: RenderedBlock): string | null {
  return block.editable === true && 'text' in block && typeof block.text === 'string' ? block.text : null;
}

function BlockContent({ block, edit, onEdit, disabled }: { block: RenderedBlock; edit: string | undefined; onEdit: (text: string) => void; disabled: boolean }) {
  let rendered: React.ReactNode;
  if ('text' in block) {
    const heading = block.type === 'h1' || block.type === 'h2' || block.type === 'h3' || block.type === 'h4';
    rendered = <div className={heading ? 'text-lg font-semibold text-gray-900' : 'text-sm leading-6 text-gray-800'} dangerouslySetInnerHTML={{ __html: block.text }} />;
  } else if (block.type === 'list') {
    rendered = <ul className="list-inside list-disc space-y-1 text-sm text-gray-800">{block.items.map((item, index) => <li key={index}>{typeof item === 'string' ? item : <><span>{item.text}</span>{item.items.length > 0 && <ul className="ml-5 list-disc">{item.items.map((child, childIndex) => <li key={childIndex}>{child}</li>)}</ul>}</>}</li>)}</ul>;
  } else if (block.type === 'table') {
    rendered = <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-gray-50"><tr>{block.header.map((cell, index) => <th key={index} className="px-3 py-2 text-left font-medium">{cell}</th>)}</tr></thead><tbody className="divide-y divide-gray-100">{block.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex} className="px-3 py-2">{cell}</td>)}</tr>)}</tbody></table></div>;
  } else if (block.type === 'sign') {
    rendered = <p className="text-sm font-medium">ผู้ลงนาม: {block.signer ? `${block.signer.name} · ${block.signer.title}` : block.roles.join(', ')}</p>;
  } else {
    rendered = <p className="text-sm text-gray-500">{block.type === 'cover' ? 'หน้าปกเอกสาร' : block.type === 'toc' ? 'สารบัญเอกสาร' : 'ขึ้นหน้าใหม่'}</p>;
  }
  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4">
      {rendered}
      {editableText(block) !== null && <div><label className="label">แก้ไขข้อความส่วนนี้</label><textarea className="input min-h-32 w-full text-base" value={edit ?? editableText(block) ?? ''} disabled={disabled} onChange={(event) => onEdit(event.target.value)} aria-label="แก้ไขข้อความเอกสาร" /></div>}
    </section>
  );
}

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const consequence = useConsequence();
  const documentQuery = useDocument(id);
  const catalogQuery = useDocKitCatalog();
  const eventsQuery = useDocumentEvents(id);
  const previewQuery = useDocumentPreview(id);
  const pdfQuery = useDocumentPdf(id);
  const patch = usePatchDocument();
  const refresh = useRefreshDocument();
  const review = useReviewDocument();
  const reopen = useReopenDocument();
  const retry = useRetryDocument();
  const publish = usePublishDocument();
  const newVersion = useCreateDocumentVersion();
  const cancel = useCancelDocument();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reviewDue, setReviewDue] = useState('');
  const [ackMode, setAckMode] = useState<AckMode>('none');
  const [scope, setScope] = useState<DocumentAudience['scope']>('all');
  const [scopeIds, setScopeIds] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [working, setWorking] = useState<string | null>(null);

  const document = !isUnavailable(documentQuery.data) && documentQuery.data ? documentQuery.data.document : undefined;
  useEffect(() => {
    if (!document) return;
    setEdits({});
    setEffectiveFrom(document.effective_from);
    setReviewDue(document.review_due ?? '');
    setAckMode(document.ack_mode);
    setScope(document.audience.scope);
    setScopeIds(scopeValues(document.audience).join(', '));
  }, [document?.id, document?.revision]);

  useEffect(() => {
    if (!previewQuery.data) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(previewQuery.data);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewQuery.data]);

  async function execute(label: string, success: string, task: () => Promise<DocKitResponse<object>>) {
    if (!id || working) return;
    setWorking(label);
    try {
      const response = await task();
      if (isUnavailable(response)) { toast.error(response.unavailable); return; }
      toast.success(success);
      await documentQuery.refetch();
    } catch (error) {
      toast.error(actionError(error, `${success}ไม่สำเร็จ กรุณาลองใหม่`));
    } finally {
      setWorking(null);
    }
  }

  async function saveDraft() {
    if (!document) return;
    await execute('save', 'บันทึกการแก้ไขแล้ว', () => patch.mutateAsync({
      id: document.id,
      body: {
        expected_revision: document.revision,
        edits,
        effective_from: effectiveFrom,
        audience: buildAudience(scope, scopeIds),
        ack_mode: ackMode,
        ...(reviewDue ? { review_due: reviewDue } : {}),
      },
    }));
  }

  async function refreshDraft() {
    if (!document) return;
    const confirmed = await consequence({ title: 'ปรับร่างจากข้อมูลล่าสุด', intro: 'ระบบจะสร้างเนื้อหาใหม่จากข้อมูลองค์กรและเทมเพลตฉบับที่เผยแพร่ล่าสุด', effects: ['ข้อความที่แก้ไว้จะคงอยู่เฉพาะส่วนที่ฐานเนื้อหายังตรงกัน'], confirmText: 'ปรับร่างเอกสาร' });
    if (confirmed) await execute('refresh', 'ปรับร่างเอกสารแล้ว', () => refresh.mutateAsync({ id: document.id, body: { expected_revision: document.revision } }));
  }

  async function reviewDocument() {
    if (!document?.pdf_sha256) { toast.error('ยังไม่มีไฟล์ PDF สำหรับตรวจเอกสาร'); return; }
    await execute('review', 'ตรวจเอกสารแล้ว', () => review.mutateAsync({ id: document.id, body: { expected_revision: document.revision, pdf_sha256: document.pdf_sha256! } }));
  }

  async function reopenDocument() {
    if (!document) return;
    await execute('reopen', 'เปิดร่างเอกสารเพื่อแก้ไขแล้ว', () => reopen.mutateAsync({ id: document.id, body: { expected_revision: document.revision } }));
  }

  async function retryDocument() {
    if (!document) return;
    await execute('retry', 'สั่งทำ PDF ใหม่แล้ว', () => retry.mutateAsync({ id: document.id }));
  }

  async function publishDocument() {
    if (!document?.pdf_sha256) { toast.error('ยังไม่มีไฟล์ PDF สำหรับเผยแพร่'); return; }
    const confirmed = await consequence({ title: 'เผยแพร่เอกสาร', intro: 'พนักงานในขอบเขตจะเห็นเอกสารและต้องรับทราบหรือลงนามตามที่กำหนด', effects: ['เมื่อเผยแพร่แล้ว ต้องออกฉบับใหม่หากต้องแก้ไขเนื้อหา'], confirmText: 'เผยแพร่เอกสาร' });
    if (confirmed) await execute('publish', 'เผยแพร่เอกสารแล้ว', () => publish.mutateAsync({ id: document.id, body: { expected_revision: document.revision, pdf_sha256: document.pdf_sha256! } }));
  }

  async function createNewVersion() {
    if (!document) return;
    const confirmed = await consequence({ title: 'ออกเอกสารฉบับใหม่', intro: 'ระบบจะสร้างร่างฉบับใหม่จากข้อมูลและเทมเพลตล่าสุด โดยยังคงฉบับเผยแพร่เดิมไว้', effects: ['ต้องตรวจและเผยแพร่ฉบับใหม่ก่อนจะแทนที่ฉบับเดิม'], confirmText: 'สร้างฉบับใหม่' });
    if (confirmed) await execute('new-version', 'สร้างร่างฉบับใหม่แล้ว', () => newVersion.mutateAsync({ id: document.id }));
  }

  async function cancelDocument() {
    if (!document) return;
    if (!cancelReason.trim()) { toast.error('กรุณาระบุเหตุผลที่ยกเลิก'); return; }
    const confirmed = await consequence({ title: 'ยกเลิกเอกสาร', intro: 'เอกสารฉบับนี้จะไม่สามารถตรวจหรือเผยแพร่ต่อได้', lines: [{ label: 'เหตุผล', value: cancelReason.trim() }], effects: ['หากต้องการใช้อีก ต้องสร้างร่างเอกสารใหม่'], confirmText: 'ยกเลิกเอกสาร', danger: true });
    if (confirmed) await execute('cancel', 'ยกเลิกเอกสารแล้ว', () => cancel.mutateAsync({ id: document.id, body: { reason: cancelReason.trim() } }));
  }

  async function openPdf() {
    if (working) return;
    const tab = window.open('', '_blank');
    setWorking('pdf');
    try {
      const result = pdfQuery.data ? { data: pdfQuery.data } : await pdfQuery.refetch();
      if (!result.data) throw new Error('ไม่พบไฟล์ PDF');
      const url = URL.createObjectURL(result.data);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
      toast.success('เปิดไฟล์ PDF แล้ว');
    } catch (error) {
      tab?.close();
      toast.error(actionError(error, 'เปิดไฟล์ PDF ไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setWorking(null);
    }
  }

  if (documentQuery.isLoading) return <AdminLayout title="รายละเอียดเอกสาร"><DetailSkeleton /></AdminLayout>;
  if (documentQuery.error || !documentQuery.data) return <AdminLayout title="รายละเอียดเอกสาร"><LoadError error={documentQuery.error} onRetry={() => void documentQuery.refetch()} /></AdminLayout>;
  if (isUnavailable(documentQuery.data)) return <AdminLayout title="รายละเอียดเอกสาร"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{documentQuery.data.unavailable}</div></AdminLayout>;
  if (!document) return <AdminLayout title="รายละเอียดเอกสาร"><EmptyState icon={FileText} title="ไม่พบเอกสาร" /></AdminLayout>;

  const warnings: ComplianceItem[] = document.warnings?.map((warning) => {
    const question = !isUnavailable(catalogQuery.data) ? catalogQuery.data?.questions.find((item) => item.key === warning.key) : undefined;
    return { level: 'warn', title: question ? `ยังไม่ได้ตอบ: ${question.label}` : 'ยังมีข้อมูลที่ไม่ได้ตอบ', detail: 'ตรวจข้อมูลในขั้นตอนจัดทำเอกสารก่อนตรวจหรือเผยแพร่', action: { label: 'ไปกรอกข้อมูล', to: `/hr/doc-kit/wizard/${question?.page ?? 1}` } };
  }) ?? [];
  const events = !isUnavailable(eventsQuery.data) && eventsQuery.data ? [...eventsQuery.data.events].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)) : [];
  const hasPdf = Boolean(document.pdf_sha256);
  const isFailed = document.job?.state === 'failed';
  const busy = Boolean(working);
  return (
    <AdminLayout title="รายละเอียดเอกสาร" subtitle={document.title}>
      <div className="max-w-5xl space-y-4 pb-8">
        <section className="card p-5 space-y-4"><div className="flex items-start justify-between gap-3 flex-wrap"><div><h2 className="font-semibold">{document.title}</h2><p className="mt-1 text-sm text-gray-600">เลขเอกสาร {document.code} · ฉบับที่ {document.doc_version}</p></div><span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">{document.status === 'draft' ? 'ร่าง' : document.status === 'approved' ? 'ตรวจแล้ว' : document.status === 'published' ? 'เผยแพร่แล้ว' : document.status === 'archived' ? 'เก็บถาวร' : 'ยกเลิกแล้ว'}</span></div><dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4"><div><dt className="text-gray-500">วันมีผล</dt><dd className="font-medium">{document.effective_from}</dd></div><div><dt className="text-gray-500">กลุ่มผู้รับ</dt><dd className="font-medium">{audienceLabel(document.audience)}</dd></div><div><dt className="text-gray-500">วิธีรับทราบ</dt><dd className="font-medium">{ACK_LABEL[document.ack_mode]}</dd></div><div><dt className="text-gray-500">เนื้อหา</dt><dd className="break-all font-medium">{document.content_sha256}</dd></div></dl><div className="flex flex-wrap gap-2"><button className="btn-secondary min-h-11" disabled={busy || pdfQuery.isFetching} onClick={() => void openPdf()}><FileText className="h-4 w-4" /> {working === 'pdf' ? 'กำลังเปิดไฟล์' : 'เปิด PDF'}</button><button className="btn-secondary min-h-11" disabled={busy || previewQuery.isFetching} onClick={() => void previewQuery.refetch()}><Eye className="h-4 w-4" /> {previewQuery.isFetching ? 'กำลังโหลดตัวอย่าง' : 'โหลดตัวอย่างใหม่'}</button></div></section>

        {warnings.length > 0 && <ComplianceAlertList items={warnings} />}

        {document.status === 'draft' && <section className="card p-5 space-y-4"><div><h2 className="font-semibold">แก้ไขร่างเอกสาร</h2><p className="mt-1 text-sm text-gray-600">แก้ได้เฉพาะข้อมูลและส่วนเนื้อหาที่อนุญาต</p></div><div className="grid gap-3 sm:grid-cols-2"><div><label className="label">วันที่มีผล</label><input className="input min-h-11 w-full text-base" type="date" value={effectiveFrom} disabled={busy} onChange={(event) => setEffectiveFrom(event.target.value)} /></div><div><label className="label">วันทบทวน</label><input className="input min-h-11 w-full text-base" type="date" value={reviewDue} disabled={busy} onChange={(event) => setReviewDue(event.target.value)} /></div><div><label className="label">วิธีรับทราบ</label><select className="input min-h-11 w-full text-base" value={ackMode} disabled={busy} onChange={(event) => setAckMode(event.target.value as AckMode)}>{(Object.keys(ACK_LABEL) as AckMode[]).map((mode) => <option key={mode} value={mode}>{ACK_LABEL[mode]}</option>)}</select></div><div><label className="label">กลุ่มผู้รับ</label><select className="input min-h-11 w-full text-base" value={scope} disabled={busy} onChange={(event) => setScope(event.target.value as DocumentAudience['scope'])}><option value="all">พนักงานทุกคน</option><option value="dept">หน่วยงาน</option><option value="branch">สาขา</option><option value="emp">พนักงานรายบุคคล</option></select></div></div>{scope !== 'all' && <div><label className="label">รหัสที่เลือก (คั่นด้วยจุลภาค)</label><input className="input min-h-11 w-full text-base" value={scopeIds} disabled={busy} onChange={(event) => setScopeIds(event.target.value)} /></div>}<div className="space-y-3"><h3 className="font-medium">เนื้อหาเอกสาร</h3>{document.content.blocks.map((block) => <BlockContent key={block.id} block={block} edit={edits[block.id]} disabled={busy} onEdit={(text) => setEdits((current) => ({ ...current, [block.id]: text }))} />)}</div><div className="flex flex-wrap gap-2"><button className="btn-cta min-h-11" disabled={busy} onClick={() => void saveDraft()}><Save className="h-4 w-4" /> {working === 'save' ? 'กำลังบันทึก' : 'บันทึกการแก้ไข'}</button><button className="btn-secondary min-h-11" disabled={busy} onClick={() => void refreshDraft()}><RefreshCw className="h-4 w-4" /> {working === 'refresh' ? 'กำลังปรับร่าง' : 'ปรับร่างจากข้อมูลล่าสุด'}</button></div></section>}

        {document.status !== 'draft' && <section className="card p-5 space-y-3"><h2 className="font-semibold">เนื้อหาเอกสาร</h2>{document.content.blocks.map((block) => <BlockContent key={block.id} block={block} disabled={true} edit={undefined} onEdit={() => undefined} />)}</section>}

        <section className="card p-5 space-y-3"><h2 className="font-semibold">ดำเนินการกับเอกสาร</h2>{document.status === 'draft' && <div className="flex flex-wrap gap-2">{hasPdf && <button className="btn-cta min-h-11" disabled={busy} onClick={() => void reviewDocument()}><CheckCircle2 className="h-4 w-4" /> {working === 'review' ? 'กำลังบันทึกการตรวจ' : 'ตรวจเอกสาร'} </button>}{isFailed && <button className="btn-secondary min-h-11" disabled={busy} onClick={() => void retryDocument()}><RefreshCw className="h-4 w-4" /> {working === 'retry' ? 'กำลังสั่งทำใหม่' : 'ลองทำ PDF ใหม่'}</button>}{hasPdf && <span className="w-full break-all text-xs text-gray-500">รหัส PDF ที่กำลังตรวจ: {document.pdf_sha256}</span>}</div>}{document.status === 'approved' && <div className="flex flex-wrap gap-2"><button className="btn-cta min-h-11" disabled={busy || !hasPdf} onClick={() => void publishDocument()}><Send className="h-4 w-4" /> {working === 'publish' ? 'กำลังเผยแพร่' : 'เผยแพร่เอกสาร'}</button><button className="btn-secondary min-h-11" disabled={busy} onClick={() => void reopenDocument()}><RotateCcw className="h-4 w-4" /> {working === 'reopen' ? 'กำลังเปิดร่าง' : 'เปิดร่างเพื่อแก้ไข'}</button></div>}{document.status === 'published' && <button className="btn-secondary min-h-11" disabled={busy} onClick={() => void createNewVersion()}><FilePlus2 className="h-4 w-4" /> {working === 'new-version' ? 'กำลังสร้างฉบับใหม่' : 'ออกฉบับใหม่'}</button>}{document.status !== 'published' && document.status !== 'cancelled' && document.status !== 'archived' && <div className="space-y-2 border-t border-gray-100 pt-4"><label className="label">เหตุผลที่ยกเลิก</label><input className="input min-h-11 w-full text-base" value={cancelReason} disabled={busy} onChange={(event) => setCancelReason(event.target.value)} /><button className="btn-danger mt-2 min-h-11" disabled={busy} onClick={() => void cancelDocument()}><XCircle className="h-4 w-4" /> {working === 'cancel' ? 'กำลังยกเลิก' : 'ยกเลิกเอกสาร'}</button></div>}</section>

        <section className="card p-5 space-y-3"><h2 className="font-semibold">ตัวอย่างเอกสาร</h2>{previewQuery.isLoading || previewQuery.isFetching ? <div className="h-64 animate-pulse rounded bg-gray-100" aria-label="กำลังโหลดตัวอย่างเอกสาร" /> : previewQuery.error ? <LoadError error={previewQuery.error} onRetry={() => void previewQuery.refetch()} /> : previewUrl ? <iframe title="ตัวอย่างเอกสาร" className="h-[32rem] w-full rounded border border-gray-200" src={previewUrl} /> : <EmptyState icon={Eye} title="ยังไม่มีตัวอย่างเอกสาร" detail="กดโหลดตัวอย่างใหม่เพื่อตรวจเนื้อหา" />}</section>

        <section className="card p-5 space-y-3"><h2 className="font-semibold">ประวัติเอกสาร</h2>{eventsQuery.isLoading ? <div className="h-24 animate-pulse rounded bg-gray-100" aria-label="กำลังโหลดประวัติเอกสาร" /> : eventsQuery.error ? <LoadError error={eventsQuery.error} onRetry={() => void eventsQuery.refetch()} /> : events.length === 0 ? <p className="text-sm text-gray-500">ยังไม่มีประวัติเอกสาร</p> : <ol className="space-y-3 border-l-2 border-gray-100 pl-4">{events.map((event, index) => <li key={`${event.occurred_at}-${index}`} className="relative text-sm"><span className="absolute -left-[1.35rem] top-1 h-2.5 w-2.5 rounded-full bg-brand-500" /><p className="font-medium">{event.event_type}</p><p className="text-gray-600">{dateTime(event.occurred_at)}</p>{event.actor_id && <p className="text-xs text-gray-500">ผู้ดำเนินการ {event.actor_id}</p>}</li>)}</ol>}</section>
      </div>
    </AdminLayout>
  );
}
